import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { seoHead } from "@/lib/seo";
import { supabase } from "@/integrations/supabase/client";
import { paymentsConfigured } from "@/lib/stripe";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { StayEmbeddedCheckout } from "@/components/StayEmbeddedCheckout";
import {
  CalendarDays,
  CheckCircle2,
  Clock,
  MapPin,
  Search,
  Users,
  Ship,
  CreditCard,
} from "lucide-react";

/**
 * Guest portal. Guests have no accounts — they look up their own stay with the
 * email they booked with plus the confirmation code from their approval email.
 * The lookup runs through the `lookup_booking` database function, which returns
 * one booking and never lists others.
 */
export const Route = createFileRoute("/my-stay")({
  head: () =>
    seoHead({
      path: "/my-stay",
      title: "My Stay — Coffman Cove Lodging",
      description:
        "Look up your confirmed Coffman Cove Lodging stay with your email and confirmation code to see dates, rates and check-in details.",
    }),
  component: MyStayPage,
});

const DOCK = "Coffman Cove city dock — 306 Harbor Ave, Coffman Cove, AK 99918";

type Stay = {
  id: string;
  name: string;
  status: string;
  arrival_date: string | null;
  departure_date: string | null;
  party_size: number | null;
  package: string | null;
  notes: string | null;
  confirmation_code: string;
  created_at: string;
  order_status: string | null;
  order_total_cents: number | null;
  order_items: unknown;
  extra_charges?: unknown;
  extra_charges_total_cents?: number | null;
};

type OrderLine = { name?: string; qty?: number; unit_price_cents?: number };
type ExtraCharge = {
  label?: string;
  amount_cents?: number;
  note?: string | null;
  charge_date?: string | null;
  created_at?: string | null;
};

/** Groups extra charges by the night they belong to, oldest night first. */
function chargesByNight(charges: ExtraCharge[]) {
  const groups = new Map<string, ExtraCharge[]>();
  for (const c of charges) {
    const day = (c.charge_date ?? c.created_at ?? "").slice(0, 10) || "undated";
    const list = groups.get(day) ?? [];
    list.push(c);
    groups.set(day, list);
  }
  return [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0]));
}


const inputClass =
  "w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-[var(--color-ember)]";

const money = (cents: number) =>
  (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });

function formatDate(value: string | null) {
  if (!value) return "—";
  return new Date(`${value}T00:00:00Z`).toLocaleDateString("en-US", {
    weekday: "short",
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

function nightsBetween(a: string | null, b: string | null) {
  if (!a) return 0;
  const start = new Date(`${a}T00:00:00Z`).getTime();
  const end = b ? new Date(`${b}T00:00:00Z`).getTime() : start + 86_400_000;
  return Math.max(1, Math.round((end - start) / 86_400_000));
}

function MyStayPage() {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [stay, setStay] = useState<Stay | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const lookup = useCallback(async (emailValue: string, codeValue: string) => {
    setLoading(true);
    setError(null);
    const { data, error: rpcError } = await supabase.rpc("lookup_booking", {
      _email: emailValue.trim(),
      _code: codeValue.trim(),
    });
    setLoading(false);

    if (rpcError) {
      setError("We couldn't look that up just now. Please try again in a moment.");
      return;
    }
    const row = (data as Stay[] | null)?.[0] ?? null;
    if (!row) {
      setStay(null);
      setError(
        "No stay found for that email and confirmation code. Check the code in your confirmation email, or call (907) 312-1019.",
      );
      return;
    }
    setStay(row);
  }, []);

  // A link in the confirmation email can carry both values so the guest lands
  // straight on their stay.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const e = params.get("email") ?? "";
    const c = params.get("code") ?? "";
    if (e && c) {
      setEmail(e);
      setCode(c);
      void lookup(e, c);
    }
  }, [lookup]);

  function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!email.trim() || code.trim().length < 6) {
      setError("Enter the email you booked with and your confirmation code.");
      return;
    }
    void lookup(email, code);
  }

  return (
    <main className="mx-auto max-w-3xl px-4 py-16">
      <h1 className="text-3xl font-semibold text-foreground sm:text-4xl">My stay</h1>
      <p className="mt-3 text-muted-foreground">
        Enter the email you booked with and the confirmation code from your approval email to
        see your dates, rates and check-in details.
      </p>

      <form
        onSubmit={onSubmit}
        className="mt-8 grid gap-4 rounded-2xl border border-border bg-card/60 p-6 sm:grid-cols-[1fr_auto]"
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="mb-1 block text-muted-foreground">Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClass}
              placeholder="you@example.com"
              autoComplete="email"
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-muted-foreground">Confirmation code</span>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              className={`${inputClass} font-mono tracking-widest`}
              placeholder="ABCD1234"
            />
          </label>
        </div>
        <div className="flex items-end">
          <button
            type="submit"
            disabled={loading}
            className="inline-flex h-10 items-center gap-2 rounded-md bg-[var(--color-ember)] px-4 text-sm font-medium text-background transition hover:brightness-110 disabled:opacity-60"
          >
            <Search className="h-4 w-4" />
            {loading ? "Looking…" : "Find my stay"}
          </button>
        </div>
      </form>

      {error && (
        <p className="mt-4 rounded-md border border-border bg-card/60 p-4 text-sm text-muted-foreground">
          {error}
        </p>
      )}

      {stay && <StayCard stay={stay} email={email} code={code} />}

      <p className="mt-10 text-sm text-muted-foreground">
        Need to change something?{" "}
        <a className="underline" href="mailto:booking@coffmancovelodging.com">
          booking@coffmancovelodging.com
        </a>{" "}
        or (907) 312-1019. Not booked yet? <Link className="underline" to="/booking">Request your dates</Link>.
      </p>
    </main>
  );
}

function StayCard({ stay, email, code }: { stay: Stay; email: string; code: string }) {
  const [payOpen, setPayOpen] = useState(false);
  const nights = nightsBetween(stay.arrival_date, stay.departure_date);
  const approved = stay.status.toLowerCase() === "approved";
  const items = Array.isArray(stay.order_items) ? (stay.order_items as OrderLine[]) : [];
  const charges = Array.isArray(stay.extra_charges) ? (stay.extra_charges as ExtraCharge[]) : [];
  const chargesTotal =
    stay.extra_charges_total_cents ??
    charges.reduce((sum, c) => sum + (c.amount_cents ?? 0), 0);
  const grandTotal = (stay.order_total_cents ?? 0) + chargesTotal;

  const paid =
    stay.order_status != null &&
    ["paid", "captured", "complete", "completed", "fulfilled"].includes(
      stay.order_status.toLowerCase(),
    );

  return (
    <section className="mt-8 rounded-2xl border border-border bg-card/60 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-semibold text-foreground">
          {stay.name}
          {stay.package ? ` — ${stay.package}` : ""}
        </h2>
        <span
          className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${
            approved
              ? "bg-[var(--color-ember)]/20 text-[var(--color-ember)]"
              : "bg-muted text-muted-foreground"
          }`}
        >
          {approved ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />}
          {approved ? "Confirmed" : `Status: ${stay.status}`}
        </span>
      </div>

      {!approved && (
        <p className="mt-3 text-sm text-muted-foreground">
          We have your request and will confirm your dates by email shortly.
        </p>
      )}

      <dl className="mt-6 grid gap-4 sm:grid-cols-2">
        <Detail icon={CalendarDays} label="Arrival" value={formatDate(stay.arrival_date)} />
        <Detail icon={CalendarDays} label="Departure" value={formatDate(stay.departure_date)} />
        <Detail icon={Clock} label="Nights" value={String(nights)} />
        <Detail
          icon={Users}
          label="Party size"
          value={stay.party_size ? String(stay.party_size) : "—"}
        />
        <Detail icon={MapPin} label="Dock & check-in" value={DOCK} />
        <Detail icon={Ship} label="Confirmation code" value={stay.confirmation_code} mono />
      </dl>

      {items.length > 0 && (
        <div className="mt-6 rounded-xl border border-border bg-background/40 p-4">
          <h3 className="text-sm font-semibold text-foreground">Your rates</h3>
          <ul className="mt-3 space-y-1 text-sm text-muted-foreground">
            {items.map((line, i) => (
              <li key={i} className="flex justify-between gap-4">
                <span>
                  {line.name}
                  {line.qty && line.qty > 1 ? ` × ${line.qty}` : ""}
                </span>
                <span>
                  {money((line.unit_price_cents ?? 0) * (line.qty ?? 1))}
                </span>
              </li>
            ))}
          </ul>
          {stay.order_total_cents != null && (
            <p className="mt-3 flex justify-between border-t border-border/60 pt-3 text-sm font-semibold text-foreground">
              <span>Estimated total</span>
              <span>{money(stay.order_total_cents)}</span>
            </p>
          )}
          <p className="mt-3 text-xs text-muted-foreground">
            {paid
              ? "Payment received — thank you."
              : "Settled on arrival. Rental prices exclude fuel and any damage to equipment or vessels."}{" "}
            All fishing is self-guided — you run your own boat or skiff.
          </p>
        </div>
      )}

      {charges.length > 0 && (
        <div className="mt-6 rounded-xl border border-border bg-background/40 p-4">
          <h3 className="text-sm font-semibold text-foreground">Fuel &amp; other charges</h3>
          <div className="mt-3 space-y-4">
            {chargesByNight(charges).map(([night, lines]) => (
              <div key={night}>
                <p className="flex justify-between gap-4 text-xs font-semibold uppercase tracking-wider text-foreground">
                  <span>{night === "undated" ? "During your stay" : formatDate(night)}</span>
                  <span className="tabular-nums">
                    {money(lines.reduce((sum, l) => sum + (l.amount_cents ?? 0), 0))}
                  </span>
                </p>
                <ul className="mt-1 space-y-1 text-sm text-muted-foreground">
                  {lines.map((line, i) => (
                    <li key={i} className="flex justify-between gap-4">
                      <span>
                        {line.label}
                        {line.note ? ` — ${line.note}` : ""}
                      </span>
                      <span className="tabular-nums">{money(line.amount_cents ?? 0)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="mt-3 flex justify-between border-t border-border/60 pt-3 text-sm font-semibold text-foreground">
            <span>Charges total</span>
            <span>{money(chargesTotal)}</span>
          </p>
          {stay.order_total_cents != null && (
            <p className="mt-2 flex justify-between text-sm text-muted-foreground">
              <span>Rates plus charges</span>
              <span>{money(grandTotal)}</span>
            </p>
          )}
          <p className="mt-3 text-xs text-muted-foreground">
            Added by the lodge after your stay was confirmed — fuel, ice and any other extras are
            settled on departure.
          </p>
        </div>
      )}


      {approved && !paid && grandTotal > 0 && paymentsConfigured() && (
        <div className="mt-6 rounded-xl border border-[var(--color-ember)]/40 bg-background/40 p-4">
          <h3 className="text-sm font-semibold text-foreground">Pay for your stay</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Rooms, boats and any extras in one payment — {money(grandTotal)} total. You can
            still settle in person if you'd rather.
          </p>
          <p className="mt-2 text-sm">
            <Link
              to="/my-stay/invoice"
              search={{ email, code }}
              className="underline text-foreground"
            >
              View your itemised invoice
            </Link>
          </p>
          {payOpen ? (
            <div className="mt-4">
              <PaymentTestModeBanner />
              <StayEmbeddedCheckout
                email={email}
                code={code}
                returnUrl={`${window.location.origin}/checkout/return?stay=1`}
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setPayOpen(true)}
              className="mt-4 inline-flex h-10 items-center gap-2 rounded-md bg-[var(--color-ember)] px-4 text-sm font-medium text-[var(--color-ember-foreground)] transition hover:brightness-110"
            >
              <CreditCard className="h-4 w-4" />
              Pay {money(grandTotal)} now
            </button>
          )}
        </div>
      )}

      {stay.notes && (
        <div className="mt-6">
          <h3 className="text-sm font-semibold text-foreground">Your notes</h3>
          <p className="mt-1 text-sm text-muted-foreground">{stay.notes}</p>
        </div>
      )}
    </section>
  );
}

function Detail({
  icon: Icon,
  label,
  value,
  mono,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="rounded-xl border border-border bg-background/40 p-4">
      <dt className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
        <Icon className="h-4 w-4" />
        {label}
      </dt>
      <dd className={`mt-1 text-sm text-foreground ${mono ? "font-mono tracking-widest" : ""}`}>
        {value}
      </dd>
    </div>
  );
}
